import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../api';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, AreaChart, Area, Legend,
} from 'recharts';

const StatsTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="ap-tooltip">
            <p className="ap-tooltip-label">{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="ap-tooltip-row" style={{ color: p.color }}>
                    {p.name}: <strong>{p.value}</strong>
                </p>
            ))}
        </div>
    );
};

const ProfileStats = ({ userId }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) return;
        setLoading(true);
        axios.get(`${API_BASE}/data/get-user-stats/${userId}`)
            .then(res => setStats(res.data))
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    }, [userId]);

    if (loading || !stats) return null;

    const totalPicks = stats.total_picks ?? 0;
    const hasSettledPicks = Object.values(stats.by_sport ?? {}).some(v => v.wins + v.losses > 0);

    // Sport chart — only include sports with at least 1 settled pick
    const sportData = ['NBA', 'EPL', 'MLS', 'MLB']
        .filter(s => stats.by_sport?.[s] && (stats.by_sport[s].wins + stats.by_sport[s].losses) > 0)
        .map(s => ({
            sport: s,
            Wins: stats.by_sport[s].wins,
            Losses: stats.by_sport[s].losses,
        }));

    const streak = stats.current_streak;
    const streakText = streak ? `${streak.count}${streak.type}` : '—';
    const streakClass = streak?.type === 'W' ? 'green' : streak ? 'red' : '';

    // recent_picks come newest-first from backend; reverse to show oldest→newest left→right
    const formChips = [...(stats.recent_picks ?? [])].slice(0, 10).reverse();

    const sportChartHeight = Math.max(120, sportData.length * 60);

    return (
        <>
            {/* ── Pick Statistics card ── */}
            <div className="ap-card">
                <p className="ap-card-title">Pick Statistics</p>

                {!hasSettledPicks ? (
                    <p className="ap-stats-empty">
                        No settled picks yet. Make some picks to unlock your stats!
                    </p>
                ) : (
                    <>
                        {/* Quick stat tiles */}
                        <div className="ap-stats-quick-row">
                            <div className="ap-quick-tile">
                                <div className="ap-quick-value">{totalPicks}</div>
                                <div className="ap-stat-label">Total Picks</div>
                            </div>
                            <div className="ap-quick-tile">
                                <div className={`ap-quick-value ${streakClass}`}>{streakText}</div>
                                <div className="ap-stat-label">Current Streak</div>
                            </div>
                            <div className="ap-quick-tile">
                                <div className="ap-quick-value green">{stats.best_win_streak ?? 0}</div>
                                <div className="ap-stat-label">Best Win Streak</div>
                            </div>
                        </div>

                        {/* Win Rate by Sport */}
                        {sportData.length > 0 && (
                            <div className="ap-chart-section">
                                <p className="ap-section-label">Win Rate by Sport</p>
                                <ResponsiveContainer width="100%" height={sportChartHeight}>
                                    <BarChart
                                        layout="vertical"
                                        data={sportData}
                                        margin={{ top: 4, right: 20, left: 4, bottom: 4 }}
                                        barSize={13}
                                        barCategoryGap="38%"
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="rgba(255,255,255,0.05)"
                                            horizontal={false}
                                        />
                                        <XAxis
                                            type="number"
                                            tick={{ fill: '#6a7492', fontSize: 10 }}
                                            axisLine={false}
                                            tickLine={false}
                                            allowDecimals={false}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="sport"
                                            tick={{ fill: '#dce2f0', fontSize: 11, fontWeight: 700 }}
                                            axisLine={false}
                                            tickLine={false}
                                            width={38}
                                        />
                                        <Tooltip
                                            content={<StatsTooltip />}
                                            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                                        />
                                        <Bar dataKey="Wins"   fill="#22c55e" radius={[0, 4, 4, 0]} />
                                        <Bar dataKey="Losses" fill="#ef4444" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {/* Recent Form */}
                        {formChips.length > 0 && (
                            <div className="ap-chart-section">
                                <p className="ap-section-label">
                                    Recent Form{' '}
                                    <span className="ap-section-sub">last {formChips.length} picks</span>
                                </p>
                                <div className="ap-form-row">
                                    {formChips.map((pick, i) => (
                                        <div
                                            key={i}
                                            className={`ap-form-chip ${pick.result === 1 ? 'win' : 'loss'}`}
                                            title={pick.picked_team}
                                        >
                                            <span className="ap-form-chip-result">
                                                {pick.result === 1 ? 'W' : 'L'}
                                            </span>
                                            <span className="ap-form-chip-sport">{pick.sport}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Most Picked Teams */}
                        {stats.most_picked_teams?.filter(t => t.team !== 'Draw').length > 0 && (
                            <div className="ap-chart-section">
                                <p className="ap-section-label">Most Picked Teams</p>
                                <table className="ap-picks-table">
                                    <thead>
                                        <tr>
                                            <th>Team</th>
                                            <th>Sport</th>
                                            <th>Picks</th>
                                            <th>Win Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.most_picked_teams.filter(t => t.team !== 'Draw').map((t, i) => {
                                            const wr = t.count > 0
                                                ? Math.round((t.wins / t.count) * 100)
                                                : 0;
                                            return (
                                                <tr key={i}>
                                                    <td>{t.team}</td>
                                                    <td>
                                                        <span className="ap-team-sport">{t.sport}</span>
                                                    </td>
                                                    <td>{t.count}</td>
                                                    <td className={t.wins > 0 && wr >= 50 ? 'green' : t.wins === 0 ? 'red' : 'red'}>
                                                        {wr}%
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Monthly Performance card (only if >= 2 months of data) ── */}
            {stats.monthly?.length >= 2 && (
                <div className="ap-card">
                    <p className="ap-card-title">Monthly Performance</p>
                    <ResponsiveContainer width="100%" height={210}>
                        <AreaChart
                            data={stats.monthly}
                            margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="gradWins" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor="#22c55e" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}   />
                                </linearGradient>
                                <linearGradient id="gradLosses" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}   />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="rgba(255,255,255,0.05)"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="month"
                                tick={{ fill: '#6a7492', fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fill: '#6a7492', fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <Tooltip content={<StatsTooltip />} />
                            <Legend
                                wrapperStyle={{
                                    fontSize: '11px',
                                    color: '#6a7492',
                                    paddingTop: '8px',
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="wins"
                                name="Wins"
                                stroke="#22c55e"
                                strokeWidth={2}
                                fill="url(#gradWins)"
                                dot={{ fill: '#22c55e', r: 3, strokeWidth: 0 }}
                                activeDot={{ r: 5, strokeWidth: 0 }}
                            />
                            <Area
                                type="monotone"
                                dataKey="losses"
                                name="Losses"
                                stroke="#ef4444"
                                strokeWidth={2}
                                fill="url(#gradLosses)"
                                dot={{ fill: '#ef4444', r: 3, strokeWidth: 0 }}
                                activeDot={{ r: 5, strokeWidth: 0 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </>
    );
};

export default ProfileStats;
